'use client';

/**
 * RecaptchaLoader Component
 * Loads the Google reCAPTCHA v3 script dynamically
 * Handles script loading, error states, and cleanup
 */

import { useEffect, useState } from 'react';
import { getRecaptchaScriptUrl, preloadRecaptcha } from '@/lib/recaptcha';

interface RecaptchaLoaderProps {
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export function RecaptchaLoader({
  onLoad,
  onError,
}: RecaptchaLoaderProps = {}) {
  const [_isLoaded, setIsLoaded] = useState(false);
  const [_error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const scriptUrl = getRecaptchaScriptUrl();

    // Skip if reCAPTCHA is not configured
    if (!scriptUrl) {
      return;
    }

    // Check if script is already loaded
    const existingScript = document.querySelector(
      `script[src^="https://www.google.com/recaptcha/"]`
    );

    if (existingScript) {
      // Script already exists, just preload
      preloadRecaptcha()
        .then(() => {
          setIsLoaded(true);
          onLoad?.();
        })
        .catch((_err) => {
          const error = new Error('Failed to initialize reCAPTCHA');
          setError(error);
          onError?.(error);
        });
      return;
    }

    // Create and load the script
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.defer = true;

    const handleLoad = () => {
      preloadRecaptcha()
        .then(() => {
          setIsLoaded(true);
          onLoad?.();
        })
        .catch((_err) => {
          const error = new Error('Failed to initialize reCAPTCHA');
          setError(error);
          onError?.(error);
        });
    };

    const handleError = () => {
      const error = new Error('Failed to load reCAPTCHA script');
      setError(error);
      onError?.(error);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);

    // Add script to document
    document.head.appendChild(script);

    // Cleanup function
    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
      // Note: We don't remove the script on unmount as it may be needed by other components
    };
  }, [onLoad, onError]);

  // This component doesn't render anything visible
  return null;
}

/**
 * Hook to check if reCAPTCHA is loaded
 */
export function useRecaptchaLoaded(): {
  isLoaded: boolean;
  error: Error | null;
} {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const scriptUrl = getRecaptchaScriptUrl();

    if (!scriptUrl) {
      setIsLoaded(false);
      return;
    }

    // Check if grecaptcha is available
    if (typeof window !== 'undefined' && window.grecaptcha) {
      preloadRecaptcha()
        .then(() => setIsLoaded(true))
        .catch((err) => {
          setError(err);
        });
    }
  }, []);

  return { isLoaded, error };
}
